import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import DataTable from 'react-data-table-component';
import { FaTrash, FaSave } from 'react-icons/fa';
import './CreateTreatment.css';
import LinkMap from '../LinkMap/LinkMap';

const TreatmentDetails = () => {
    const { id } = useParams(); // Get the treatment ID from the URL
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [treatment, setTreatment] = useState({
        Legend: '',
        Treatmentname: '',
        Cost: '',
        Inventory: [],
        Notes: '',
    });
    const [inventoryList, setInventoryList] = useState([]); // State for fetched inventory items
    const [isEditable, setIsEditable] = useState(false); // State to toggle edit mode
    const [loading, setLoading] = useState(true); // State for loading
    const [successModalVisible, setSuccessModalVisible] = useState(false); // State for success modal
    const [activeTab, setActiveTab] = useState('Inventory'); // State for active tab
    const linkData = [
        { label: 'Treatments', path: '/treatment' },
        { label: 'Treatment Details', path: '/treatment-details' },
    ];

    // Fetch treatment details by ID
    useEffect(() => {
        const fetchTreatment = async () => {
            try {
                const response = await fetch(`https://api.hgcdental.clinic/api/treatments/${id}`);
                if (!response.ok) throw new Error('Failed to fetch treatment');
                const data = await response.json();
                setTreatment(data);
            } catch (error) {
                console.error('Error fetching treatment:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTreatment();
    }, [id]);

    // Fetch inventory items from the API
    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/inventory');
                if (!response.ok) throw new Error('Failed to fetch inventory');
                const data = await response.json();
    
                // Filter out items where isArchived is true
                const filteredData = data.filter(item => !item.isArchived);
    
                setInventoryList(filteredData); // Set the filtered inventory items
            } catch (err) {
                console.error('Error fetching inventory:', err);
            }
        };
    
        fetchInventory();
    }, []);
    
    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTreatment((prev) => ({ ...prev, [name]: value }));
    };

    // Handle inventory changes
    const handleInventoryChange = (index, field, value) => {
        const updatedInventory = treatment.Inventory.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        setTreatment((prev) => ({ ...prev, Inventory: updatedInventory }));
    };

    // Add new inventory item
    const addInventoryItem = () => {
        setTreatment((prev) => ({
            ...prev,
            Inventory: [...prev.Inventory, { inventoryId: '', Quantity: 1 }],
        }));
    };

    // Delete inventory item
    const handleDeleteInventory = (index) => {
        const updatedInventory = treatment.Inventory.filter((_, i) => i !== index);
        setTreatment((prev) => ({ ...prev, Inventory: updatedInventory }));
    };

    // Handle save click
    const handleSaveClick = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/treatments/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(treatment),
            });

            if (!response.ok) throw new Error('Failed to update treatment');
            const updatedTreatment = await response.json();
            setTreatment(updatedTreatment);
            setIsEditable(false);
            setSuccessModalVisible(true);
        } catch (error) {
            console.error('Error updating treatment:', error);
        }
    };

    // Close success modal
    const closeSuccessModal = () => setSuccessModalVisible(false);

    // Handle tab click
    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };

    // Table columns for inventory
    const inventoryColumns = [
        {
            name: 'Name',
            cell: (row, index) => (
                <div>
                    <select
                        value={row.inventoryId || ''}
                        onChange={(e) => handleInventoryChange(index, 'inventoryId', e.target.value)}
                        disabled={!isEditable}
                    >
                        <option value="" disabled>
                            Select Inventory
                        </option>
                        {inventoryList.map((item) => (
                            <option key={item._id} value={item._id}>
                                {item.Name}
                            </option>
                        ))}
                    </select>

                    {row.inventoryId && (
                        <div style={{ marginTop: '5px' }}>
                            <a
                                href={`https://hgcdental.clinic/inventory-details/${row.inventoryId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: '#007bff',
                                    textDecoration: 'underline',
                                    fontSize: '0.85rem'
                                }}
                            >
                                View Item Details
                            </a>
                        </div>
                    )}
                </div>
            ),
        },        
        {
            name: 'Quantity',
            cell: (row, index) => (
                <input
                    type="number"
                    value={row.Quantity}
                    min="1"
                    onChange={(e) => handleInventoryChange(index, 'Quantity', parseInt(e.target.value, 10))}
                    disabled={!isEditable}
                />
            ),
        },
        {
            name: 'Actions',
            cell: (row, index) => (
                <FaTrash
                    className="action-icon"
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleDeleteInventory(index)}
                    disabled={!isEditable}
                />
            ),
        },
    ];

    if (loading) return <div>Loading...</div>;

     // Custom styles for DataTable
     const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
    };

    return (
        <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
            <InternalHeader moduleName="Treatment Details" toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
            <div className="create-patient-content">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="create-patient">
                    <LinkMap
                        links={linkData}
                        moduleName="Appointments"
                        moduleIcon="calendar"
                    />
                    <div>
                        {isEditable ? (
                            <button className="create-patient-button" onClick={handleSaveClick}>
                                 <FaSave /> Save
                            </button>
                        ) : (
                            <button className="create-patient-button" onClick={() => setIsEditable(true)}>
                                Edit
                            </button>
                        )}
                    </div>
                    <div className="create-patient-form">
                        <h2>Treatment Details</h2>

                        {/* Legend Field */}
                        <div className="input-group">
                            <label className="label">Legend</label>
                            <input
                                type="text"
                                name="Legend"
                                value={treatment.Legend || ''}
                                onChange={handleInputChange}
                                className="input-field"
                                disabled={!isEditable}
                            />
                        </div>

                        {/* Treatment Name Field */}
                        <div className="input-group">
                            <label className="label">Treatment Name</label>
                            <input
                                type="text"
                                name="Treatmentname"
                                value={treatment.Treatmentname || ''}
                                onChange={handleInputChange}
                                className="input-field"
                                disabled={!isEditable}
                            />
                        </div>

                        {/* Cost Field */}
                        <div className="input-group">
                            <label className="label">Cost</label>
                            <input
                                type="number"
                                name="Cost"
                                value={treatment.Cost || ''}
                                onChange={handleInputChange}
                                className="input-field"
                                disabled={!isEditable}
                            />
                        </div>

                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <span
                                onClick={() => handleTabClick('Inventory')}
                                className={`tab ${activeTab === 'Inventory' ? 'active' : ''}`}
                            >
                                Inventory
                            </span>
                            <span
                                onClick={() => handleTabClick('Notes')}
                                className={`tab ${activeTab === 'Notes' ? 'active' : ''}`}
                            >
                                Notes
                            </span>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === 'Inventory' && (
                                <div className="invoice-tab">
                                    <button onClick={addInventoryItem} className="create-button-table" disabled={!isEditable}>
                                        Add Inventory
                                    </button>
                                    <div className="modal-divider-table" />
                                    <div className="table-form-table">
                                    <DataTable
                                        columns={inventoryColumns}
                                        data={treatment.Inventory}
                                        highlightOnHover
                                        striped
                                        customStyles={customStyles}
                                    />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'Notes' && (
                                <div className="patient-details-tab">
                                    <h2>Notes</h2>
                                    <textarea
                                        className="prescription-input"
                                        placeholder="Write Notes here..."
                                        value={treatment.Notes || ''}
                                        onChange={(e) => setTreatment((prev) => ({ ...prev, Notes: e.target.value }))}
                                        disabled={!isEditable}
                                    ></textarea>
                                </div>
                            )}
                        </div>

                        {/* Success Modal */}
                        {successModalVisible && (
                            <div className="modal-overlay" onClick={closeSuccessModal}>
                                <div className="modal success-modal" onClick={(e) => e.stopPropagation()}>
                                    <h3>Success!</h3>
                                    <div className="modal-divider" />
                                    <p>Treatment details updated successfully.</p>
                                    <div className="modal-divider" />
                                    <button className="confirm-button" onClick={closeSuccessModal}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TreatmentDetails;
        //how